import { createContext, useCallback, useContext, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Promise-based replacement for `window.confirm` that renders a Shadcn
 * AlertDialog. Mount <ConfirmDialogProvider> once near the app root and call
 * `await confirm({ ... })` from anywhere via the `useConfirm()` hook.
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: "Delete moodboard?",
 *     description: "This cannot be undone.",
 *     confirmLabel: "Delete",
 *     destructive: true,
 *   });
 *   if (!ok) return;
 */
const ConfirmContext = createContext(null);

export function ConfirmDialogProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setState({
          ...opts,
          resolve,
        });
      }),
    [],
  );

  function close(result) {
    if (state?.resolve) state.resolve(result);
    setState(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog
        open={!!state}
        onOpenChange={(o) => {
          if (!o) close(false);
        }}
      >
        <AlertDialogContent data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              {state?.title || "Are you sure?"}
            </AlertDialogTitle>
            {state?.description && (
              <AlertDialogDescription className="text-stone-500">
                {state.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="rounded-full"
              data-testid="confirm-dialog-cancel"
              onClick={() => close(false)}
            >
              {state?.cancelLabel || "Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => close(true)}
              className={`rounded-full ${
                state?.destructive
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-stone-900 hover:bg-stone-800"
              }`}
            >
              {state?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx)
    throw new Error("useConfirm must be used inside <ConfirmDialogProvider>");
  return ctx;
}
